import { inject } from '@angular/core';
import { CanActivateChildFn } from '@angular/router';
import * as jwt_decode from 'jwt-decode';
import { CookiesService, SessionService } from '@shared';
import { MerchantKycStore } from '../data/store/kyc.store';

/** Cap on how long navigation may block waiting for the KYC lookup. */
const KYC_WAIT_TIMEOUT_MS = 8000;
const KYC_POLL_INTERVAL_MS = 50;

export const dashboardChildGuard: CanActivateChildFn = async (_route, _state) => {
  const cookieService = inject(CookiesService);
  const kycStore = inject(MerchantKycStore);
  const session = inject(SessionService);

  const token = cookieService.get('token');

  if (!token) {
    session.forceLogout('no-token');
    return false;
  }

  try {
    const decodedToken: { exp: number } = jwt_decode.jwtDecode(token);
    if (Date.now() >= decodedToken.exp * 1000) {
      session.forceLogout('no-token');
      return false;
    }

    // Idempotent re-arm — covers navigating back in after a session lapsed.
    session.start(token);

    if (!kycStore.businessInformation()) {
      await new Promise<void>((resolve) => {
        kycStore.getBusinessInformation();
        const startedAt = Date.now();
        const checkInterval = setInterval(() => {
          // Resolve on timeout as well as completion: a KYC request that never
          // settles must degrade to a rendered page, not a blank screen.
          if (
            !kycStore.gettingBusinessInfo() ||
            Date.now() - startedAt >= KYC_WAIT_TIMEOUT_MS
          ) {
            clearInterval(checkInterval);
            resolve();
          }
        }, KYC_POLL_INTERVAL_MS);
      });
    }

    return true;
  } catch (error) {
    console.error('Error decoding token:', error);
    session.forceLogout('no-token');
    return false;
  }
};
